import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Star, Flame, Sparkles, Lock, CheckCircle2, ClipboardCheck, ArrowRight } from 'lucide-react';
import { AppState } from '../../app/types';
import { GroupId } from '../../config/systemConfig';
import { MODULES } from '../../content';
import { useAppStore } from '../../app/hooks/useAppStore';
import { useDjaySelection } from '../../mascot';
import { Card } from '../ui/Card';
import { AchievementsSection } from './AchievementsSection';
import { MilestonesSection } from './MilestonesSection';
import { ErrorPatternsSection } from './ErrorPatternsSection';
import { DjayAvatar } from '../../mascot';
import type { DjayVariant } from '../../mascot';
import { PageHero, HeroStatPill } from '../shared/PageHero';
import { PageContainer } from '../shared/PageContainer';
import { itemVariants } from '../shared/animations';
import { WorldZone } from '../shared/WorldZone';
import { Button } from '../ui/Button';
import type { AssessmentReport } from '../../assessment';
interface ProgressPageProps {
  state: AppState;
  selectedGroup: GroupId;
  onViewReport: (report: AssessmentReport) => void;
}
export function ProgressPage({
  state,
  selectedGroup,
  onViewReport
}: ProgressPageProps) {
  const {
    getModuleMasteryForGroup,
    getAnsweredQuestions
  } = useAppStore();
  const {
    selectedVariant,
    setSelectedVariant
  } = useDjaySelection();
  // Calculate overall stats
  const totalQuestions = MODULES.reduce((total, module) => total + getAnsweredQuestions(module.id).length, 0);
  // Calculate overall progress
  const {
    masteredCount,
    inProgressCount,
    totalQuestionsAnswered
  } = useMemo(() => {
    let mastered = 0;
    let inProgress = 0;
    let totalQs = 0;
    MODULES.forEach(m => {
      const mastery = getModuleMasteryForGroup(m.id);
      const answered = getAnsweredQuestions(m.id).length;
      totalQs += answered;
      if (mastery >= 95) mastered++;else if (answered > 0 || mastery > 0) inProgress++;
    });
    return {
      masteredCount: mastered,
      inProgressCount: inProgress,
      totalQuestionsAnswered: totalQs
    };
  }, [getModuleMasteryForGroup, getAnsweredQuestions]);
  // Calculate streak (simplified for this view)
  const currentStreak = state.dailyStreak?.currentWeekDays || 0;
  const consecutiveFullWeeks = state.dailyStreak?.consecutiveFullWeeks || 0;
  // Determine unlocked special Djays based on achievements logic
  const unlockedVariants = useMemo(() => {
    const variants: {
      variant: DjayVariant;
      name: string;
      description: string;
      unlocked: boolean;
    }[] = [{
      variant: 'pixel',
      name: 'DJ Pixel',
      description: 'Honderdtal bereikt',
      unlocked: totalQuestionsAnswered >= 100
    }, {
      variant: 'blaze',
      name: 'DJ Blaze',
      description: '3 volle weken achter elkaar',
      unlocked: consecutiveFullWeeks >= 3
    }, {
      variant: 'frost',
      name: 'DJ Frost',
      description: '3 vakken afgerond',
      unlocked: masteredCount >= 3
    }, {
      variant: 'royal',
      name: 'DJ Royal',
      description: 'Alle vakken afgerond',
      unlocked: masteredCount >= MODULES.length
    }, {
      variant: 'cosmic',
      name: 'DJ Cosmic',
      description: 'Level 15 bereikt',
      unlocked: state.stats.level >= 15
    }, {
      variant: 'shadow',
      name: 'DJ Shadow',
      description: '30.000 XP behaald',
      unlocked: state.stats.xp >= 30000
    }];
    return variants;
  }, [totalQuestionsAnswered, consecutiveFullWeeks, masteredCount, state.stats.level, state.stats.xp]);
  return <PageContainer className="pb-20" data-id="element-1640">
      {/* Hero Header with Djay */}
      <motion.div variants={itemVariants} data-id="element-1641">
        <PageHero title="Mijn Voortgang" subtitle="Bekijk je leertips, mijlpalen en achievements! 📊" gradientClasses="from-indigo-500 via-violet-500 to-purple-500" djayEmotion="celebrating" djayVariant={selectedVariant} icon={TrendingUp} iconBgClass="bg-indigo-400" floatingEmojis={['📊', '⭐']} data-id="element-1642">
          <HeroStatPill icon={Star} data-id="element-1643">Level {state.stats.level}</HeroStatPill>
          <HeroStatPill icon={Sparkles} data-id="element-1644">
            {state.stats.xp.toLocaleString('nl-NL')} XP
          </HeroStatPill>
          <HeroStatPill icon={Flame} data-id="element-1645">
            {currentStreak} dagen deze week
          </HeroStatPill>
        </PageHero>
      </motion.div>

      {/* Error Patterns Section (Slimme Leertips) — FIRST for actionability */}
      <motion.div variants={itemVariants} data-id="element-1646">
        <ErrorPatternsSection state={state} selectedGroup={selectedGroup} data-id="element-1647" />
      </motion.div>

      {/* Milestones Section */}
      <motion.div variants={itemVariants} data-id="element-1648">
        <MilestonesSection state={state} selectedGroup={selectedGroup} data-id="element-1649" />
      </motion.div>

      {/* Achievements Section */}
      <motion.div variants={itemVariants} data-id="element-1650">
        <AchievementsSection state={state} totalQuestionsAnswered={totalQuestionsAnswered} masteredCount={masteredCount} currentStreak={currentStreak} data-id="element-1651" />
      </motion.div>

      {/* Mascotte Collectie */}
      <motion.div variants={itemVariants} className="space-y-4" data-id="element-1652">
        <h3 className="text-xl font-black text-slate-800 flex items-center gap-2 px-1" data-id="element-1653">
          <Sparkles className="w-6 h-6 text-orange-500" data-id="element-1654" /> Mascotte Collectie
        </h3>

        <div className="bg-white rounded-3xl p-4 sm:p-6 border-2 border-slate-100 shadow-sm" data-id="element-1655">
          {/* Horizontal scrollable row */}
          <div className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-4 -mx-2 px-2 gap-3 sm:gap-4" data-id="element-1656">
            {unlockedVariants.map((item, index) => {
            const isSelected = selectedVariant === item.variant;
            return <button key={item.variant} onClick={() => item.unlocked && !isSelected && setSelectedVariant(item.variant)} className={`relative flex flex-col items-center gap-2 min-w-[80px] snap-center transition-all ${!item.unlocked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:scale-105'}`} data-id="element-1657">
                  <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center relative transition-all ${isSelected ? 'ring-4 ring-orange-400 ring-offset-2 bg-orange-50' : item.unlocked ? 'border-2 border-slate-200 bg-slate-50 hover:border-orange-200 hover:bg-orange-50/50' : 'border-2 border-slate-200 bg-slate-100 grayscale'}`} data-id="element-1658">
                    {!item.unlocked && <div className="absolute inset-0 z-10 flex items-center justify-center rounded-full bg-slate-800/20 backdrop-blur-[1px]" data-id="element-1659">
                        <Lock className="w-4 h-4 text-slate-700" data-id="element-1660" />
                      </div>}
                    <DjayAvatar size="sm" emotion={item.unlocked ? isSelected ? 'excited' : 'celebrating' : 'sleeping'} variant={item.variant} animate={item.unlocked} data-id="element-1661" />
                  </div>

                  <div className="flex flex-col items-center" data-id="element-1662">
                    <span className={`text-[10px] sm:text-xs font-bold ${isSelected ? 'text-orange-600' : item.unlocked ? 'text-slate-700' : 'text-slate-400'}`} data-id="element-1663">
                      {item.name}
                    </span>
                    {isSelected && <span className="text-[9px] font-bold text-white bg-orange-500 px-2 py-0.5 rounded-full mt-1" data-id="element-1664">
                        Actief
                      </span>}
                  </div>
                </button>;
          })}
          </div>

          {/* Selected mascot detail */}
          {(() => {
          const selected = unlockedVariants.find(v => v.variant === selectedVariant);
          if (!selected) return null;
          return <div className="mt-2 pt-4 border-t-2 border-slate-100 flex items-center gap-4" data-id="element-1665">
                <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0" data-id="element-1666">
                  <DjayAvatar size="sm" emotion="excited" variant={selected.variant} animate data-id="element-1667" />
                </div>
                <div data-id="element-1668">
                  <h4 className="font-bold text-slate-800 text-sm" data-id="element-1669">
                    {selected.name}
                  </h4>
                  <p className="text-xs text-slate-500 font-medium" data-id="element-1670">
                    Ontgrendeld door: {selected.description}
                  </p>
                </div>
              </div>;
        })()}
        </div>
      </motion.div>
    </PageContainer>;
}